import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Pagination,
  Tabs,
  Tab,
  Button,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Build as BuildIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { formatStatus } from '../utils/formatStatus';
import { useNavigate } from 'react-router-dom';
import { portalApi, WorkOrderItem } from '../services/portalApi';
import { useTranslation } from 'react-i18next';
import { i18n } from '../i18n';

type TabValue = 'upcoming' | 'completed';

export const WorkOrders: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabValue>('upcoming');

  const limit = 10;

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await portalApi.getWorkOrders({
          limit, view: tab,
          offset: (page - 1) * limit,
        });
        if (cancelled) return;
        setWorkOrders(response.items);
        setTotal(response.total);
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [page, tab]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return t('Not scheduled');
    return new Date(dateStr).toLocaleDateString(i18n.resolvedLanguage === 'es' ? 'es-MX' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const getStatusColor = (status: string): 'info' | 'warning' | 'success' | 'default' => {
    switch (status) {
      case 'scheduled':
        return 'info';
      case 'in_progress':
        return 'warning';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const formatWorkOrderType = (type: string) => {
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const filteredWorkOrders = workOrders.filter((wo) => {
    if (tab === 'upcoming') return wo.status === 'scheduled' || wo.status === 'in_progress';
    return wo.status === 'completed';
  });

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('Work Orders')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t('View your scheduled and completed work orders')}
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, value) => { setTab(value); setPage(1); }}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label={t('Upcoming')} value="upcoming" />
        <Tab label={t('Completed')} value="completed" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Card key={i} sx={{ mb: 2 }}>
              <CardContent>
                <Skeleton width="30%" height={28} />
                <Skeleton width="60%" height={24} />
                <Skeleton width="40%" height={20} />
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : filteredWorkOrders.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            {tab === 'upcoming' ? (
              <>
                <BuildIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('No upcoming work orders')}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {t('When you have upcoming scheduled work, it will appear here')}
                </Typography>
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  {t('No completed work orders yet')}
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {t('Completed work orders and service reports will appear here')}
                </Typography>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {filteredWorkOrders.map((workOrder) => (
            <Card key={workOrder.id} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6">
                        {formatWorkOrderType(workOrder.type)}
                      </Typography>
                      <Chip
                        label={t(formatStatus(workOrder.status))}
                        size="small"
                        color={getStatusColor(workOrder.status)}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: 'text.secondary' }}>
                      <ScheduleIcon sx={{ fontSize: 18 }} />
                      <Typography>
                        {workOrder.status === 'completed' && workOrder.completedAt
                          ? `${t('Completed')} ${formatDate(workOrder.completedAt)}`
                          : formatDate(workOrder.scheduledDate)}
                      </Typography>
                    </Box>

                    {workOrder.property && (
                      <Typography color="text.secondary">
                        {workOrder.property.name || workOrder.property.address}
                      </Typography>
                    )}

                    {workOrder.description && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 2,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                        }}
                      >
                        {workOrder.description}
                      </Typography>
                    )}

                    {workOrder.notes && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          p: 1.5,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          fontStyle: 'italic',
                        }}
                      >
                        {workOrder.notes}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      {workOrder.estimatedDuration && (
                        <Typography variant="body2" color="text.secondary">
                          {t('Est. Duration')}: {formatDuration(workOrder.estimatedDuration)}
                        </Typography>
                      )}
                      {workOrder.technicianName && (
                        <Typography variant="body2" color="text.secondary">
                          {t('Technician')}: {workOrder.technicianName}
                        </Typography>
                      )}
                      <Button variant="outlined" size="small" onClick={() => navigate(`/work-orders/${workOrder.id}`)}>
                        {t('View Work Order')}
                      </Button>
                      {workOrder.serviceLogId && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AssignmentIcon />}
                          onClick={() => navigate(`/service-history/${workOrder.serviceLogId}`)}
                          sx={{ ml: 'auto' }}
                        >
                          {t(workOrder.logType === 'inspection' ? 'View Inspection Report' : 'View Service Report')}
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};
